import { useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DownloadSimple, ArrowLeft, FileText } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { useAuth } from "../lib/AuthContext";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { blendedNetForVolume, COGS_TIERS, SOFTWARE_NET_USD } from "../lib/cogs";

// Default scenarios (the four sensitivities)
const DEFAULT_SCENARIOS = {
    LOW: [3000, 8000, 18000, 32000, 50000],
    MEDIUM: [8000, 25000, 60000, 110000, 180000],
    HIGH: [15000, 50000, 130000, 280000, 500000],
    STRETCH: [25000, 100000, 300000, 700000, 1400000],
};

const ADMIN_EMAILS = ["lea@millen.com", "glenn@millen.com"];

export default function RevenueModel() {
    const { user, loading } = useAuth();
    const [scenarios, setScenarios] = useState(DEFAULT_SCENARIOS);
    const [asp, setAsp] = useState(130);
    const [netPerUnit, setNetPerUnit] = useState(80);
    const [fx, setFx] = useState(1.55);
    const [blend, setBlend] = useState(60); // bundle %
    const [downloading, setDownloading] = useState(false);
    const [applyVolumeCogs, setApplyVolumeCogs] = useState(true);

    // Recompute net-per-unit assumption when blend changes (rough volume-discount uplift in later years not modelled here for simplicity — flat net)
    function setBlendAndRecalc(b) {
        setBlend(b);
        // ASP adjusts: software $100, bundle $150
        setAsp(Math.round(100 * (1 - b/100) + 150 * (b/100)));
    }

    const rows = useMemo(() => {
        return Object.entries(scenarios).map(([name, years]) => {
            const yrs = years.map((u, i) => {
                let yearNet = netPerUnit;
                let tierLabel = null;
                let bundleNet = null;
                if (applyVolumeCogs) {
                    const result = blendedNetForVolume(u, blend);
                    yearNet = result.net_per_unit_usd;
                    tierLabel = result.tier.label;
                    bundleNet = result.bundle_net_usd;
                }
                return {
                    year: i + 1, units: u,
                    revenueUsd: u * asp,
                    netUsd: u * yearNet,
                    netPerUnit: yearNet,
                    tierLabel, bundleNet,
                };
            });
            const totalUnits = years.reduce((a, b) => a + b, 0);
            const totalRev = yrs.reduce((a, b) => a + b.revenueUsd, 0);
            const totalNet = yrs.reduce((a, b) => a + b.netUsd, 0);
            return { name, years: yrs, totalUnits, totalRev, totalNet };
        });
    }, [scenarios, asp, netPerUnit, applyVolumeCogs, blend]);

    function setYearUnits(scenarioName, yearIdx, value) {
        const v = Math.max(0, parseInt(value) || 0);
        setScenarios((s) => ({
            ...s,
            [scenarioName]: s[scenarioName].map((u, i) => (i === yearIdx ? v : u)),
        }));
    }

    async function downloadPdf() {
        setDownloading(true);
        try {
            const payload = {
                scenarios: Object.entries(scenarios).map(([name, years]) => ({
                    name,
                    years: years.map((u, i) => ({ year: i + 1, units: u })),
                })),
                asp_usd: asp,
                net_per_unit_usd: netPerUnit,
                fx_rate: fx,
                blend_pct: blend,
                apply_volume_cogs: applyVolumeCogs,
                blend_label: `${blend}% bundle / ${100 - blend}% software · ASP $${asp}${applyVolumeCogs ? " · COGS scaled by volume tier" : ` · Net $${netPerUnit}/unit (flat)`}`,
            };
            const res = await api.post("/admin/export/revenue-model.pdf", payload, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "remembr-revenue-model.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Revenue Model PDF downloaded.");
        } catch (e) {
            toast.error("PDF generation failed. Try again.");
        } finally {
            setDownloading(false);
        }
    }

    async function downloadBusinessPlan() {
        try {
            const res = await api.get("/admin/export/business-plan.pdf", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "remembr-business-plan.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Business Plan PDF downloaded.");
        } catch (e) {
            toast.error("Business Plan download failed.");
        }
    }

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><ForgetMeNotIcon size={48} className="animate-pulse" /></div>;
    }
    if (!user || user === false) return <Navigate to="/sign-in" replace />;
    if (!ADMIN_EMAILS.includes((user.email || "").toLowerCase())) return <Navigate to="/dashboard" replace />;

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="revenue-model-page">
            <SiteNav />
            <main className="pt-28 pb-24 px-6 rem-watercolor-bg min-h-screen">
                <div className="max-w-6xl mx-auto">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#5C5A59] hover:text-[#6B7DB8] mb-6" data-testid="model-back">
                        <ArrowLeft size={16} /> Back to admin
                    </Link>

                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-3">Mum & Dad global AI business</p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight" data-testid="model-heading">
                            Revenue model — <em className="italic text-[#4A5A95]">live calculator</em>
                        </h1>
                        <p className="mt-3 text-[#5C5A59] max-w-2xl">
                            Tweak units, blend, ASP and FX. The four scenarios update live. Hit Export PDF and walk into China with the numbers.
                        </p>
                    </motion.div>

                    {/* Controls */}
                    <section className="mt-10 bg-white rounded-3xl border border-[#E5E1D8]/70 shadow-rem-soft p-6 sm:p-8" data-testid="model-controls">
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
                            <Slider label="Bundle mix %" value={blend} min={0} max={100} step={5} suffix="%" onChange={setBlendAndRecalc} testid="blend-slider" />
                            <Slider label="Blended ASP (USD)" value={asp} min={80} max={170} step={5} prefix="$" onChange={setAsp} testid="asp-slider" />
                            <Slider label={applyVolumeCogs ? "Net /unit (auto, by tier)" : "Net per unit (USD)"} value={netPerUnit} min={40} max={120} step={5} prefix="$" onChange={setNetPerUnit} testid="net-slider" disabled={applyVolumeCogs} />
                            <Slider label="FX (1 USD → AUD)" value={fx} min={1.2} max={1.8} step={0.01} float onChange={setFx} testid="fx-slider" />
                        </div>

                        {/* Volume COGS toggle */}
                        <div className="mt-6 flex items-center justify-between flex-wrap gap-4 p-4 bg-[#F4F6FB] rounded-2xl border border-[#E5E1D8]/70" data-testid="cogs-toggle-row">
                            <div>
                                <p className="text-sm font-semibold text-[#2D2B2A]">Volume-based COGS scaling</p>
                                <p className="text-xs text-[#5C5A59] mt-1 max-w-xl">
                                    When ON, bundle hardware cost drops as volume rises (1K→1M+ MOQ tiers from the China factory). Net per unit is recomputed for <em className="italic">each year</em> based on that year's bundle volume.
                                </p>
                            </div>
                            <button
                                onClick={() => setApplyVolumeCogs((v) => !v)}
                                className={`relative inline-flex items-center h-7 rounded-full w-12 transition-colors ${applyVolumeCogs ? "bg-[#6B7DB8]" : "bg-[#E5E1D8]"}`}
                                data-testid="cogs-toggle"
                                aria-pressed={applyVolumeCogs}
                            >
                                <span className={`inline-block w-5 h-5 transform bg-white rounded-full transition-transform shadow ${applyVolumeCogs ? "translate-x-6" : "translate-x-1"}`} />
                            </button>
                        </div>

                        {/* Tier curve reference (visible when ON) */}
                        {applyVolumeCogs && (
                            <div className="mt-5 overflow-x-auto" data-testid="cogs-tiers-table">
                                <p className="text-xs uppercase tracking-[0.22em] text-[#6B7DB8] font-semibold mb-3">Bundle COGS tiers (15" tablet + branded headphones drop-shipped from China)</p>
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-[#8A8786] border-b border-[#E5E1D8]/70">
                                            <th className="py-2 pr-3 font-medium">Annual bundle volume</th>
                                            <th className="py-2 pr-3 text-right font-medium">Hardware COGS</th>
                                            <th className="py-2 pr-3 text-right font-medium">Logistics / payment</th>
                                            <th className="py-2 pr-3 text-right font-medium">Bundle ASP</th>
                                            <th className="py-2 pr-3 text-right font-medium">Bundle net (USD)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...COGS_TIERS].reverse().map((t) => (
                                            <tr key={t.label} className="border-b border-[#E5E1D8]/40">
                                                <td className="py-2 pr-3 text-[#2D2B2A]">{t.label === "Pilot" ? "Pilot (0 – 999)" : `${t.label} (${t.min.toLocaleString()}+)`}</td>
                                                <td className="py-2 pr-3 text-right text-[#5C5A59]">${t.bundleHardware}</td>
                                                <td className="py-2 pr-3 text-right text-[#5C5A59]">${t.bundleOther}</td>
                                                <td className="py-2 pr-3 text-right text-[#5C5A59]">$150</td>
                                                <td className="py-2 pr-3 text-right text-[#4A5A95] font-semibold">${150 - t.bundleHardware - t.bundleOther}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="mt-3 text-xs text-[#8A8786]">Software-only SKU net is held at <strong className="text-[#4A5A95]">${SOFTWARE_NET_USD}</strong> across all volumes (cloud + TTS + payment).</p>
                            </div>
                        )}

                        <div className="mt-6 flex flex-wrap gap-3">
                            <button onClick={downloadPdf} disabled={downloading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all disabled:opacity-60" data-testid="download-model-pdf">
                                <DownloadSimple size={16} weight="bold" />
                                {downloading ? "Generating…" : "Download Revenue Model PDF"}
                            </button>
                            <button onClick={downloadBusinessPlan} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F4D068] hover:bg-[#E6B844] text-[#2D2B2A] text-sm font-medium transition-all" data-testid="download-bp-pdf">
                                <FileText size={16} weight="bold" />
                                Download Business Plan PDF
                            </button>
                        </div>
                    </section>

                    {/* Summary table */}
                    <section className="mt-8 bg-white rounded-3xl border border-[#E5E1D8]/70 shadow-rem-soft p-6 sm:p-8" data-testid="summary-table">
                        <h2 className="font-serif text-2xl text-[#2D2B2A] mb-5">5-year summary</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#8A8786] border-b border-[#E5E1D8]/70">
                                        <th className="py-2 pr-4">Scenario</th>
                                        <th className="py-2 pr-4 text-right">5yr Units</th>
                                        <th className="py-2 pr-4 text-right">5yr Rev (USD)</th>
                                        <th className="py-2 pr-4 text-right">5yr Net (USD)</th>
                                        <th className="py-2 pr-4 text-right">5yr Net (AUD)</th>
                                        <th className="py-2 pr-4 text-right">Charity 5% (AUD)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.name} className="border-b border-[#E5E1D8]/40">
                                            <td className="py-3 pr-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badgeFor(r.name)}`}>{r.name}</span></td>
                                            <td className="py-3 pr-4 text-right text-[#2D2B2A]">{r.totalUnits.toLocaleString()}</td>
                                            <td className="py-3 pr-4 text-right text-[#5C5A59]">{money(r.totalRev)}</td>
                                            <td className="py-3 pr-4 text-right text-[#2D2B2A] font-medium">{money(r.totalNet)}</td>
                                            <td className="py-3 pr-4 text-right text-[#4A5A95] font-medium">{moneyAud(r.totalNet * fx)}</td>
                                            <td className="py-3 pr-4 text-right text-[#5C5A59]">{moneyAud(r.totalNet * 0.05 * fx)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Year-by-year per scenario (editable) */}
                    {rows.map((r) => (
                        <section key={r.name} className="mt-8 bg-white rounded-3xl border border-[#E5E1D8]/70 shadow-rem-soft p-6 sm:p-8" data-testid={`scenario-${r.name}`}>
                            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                                <h2 className="font-serif text-2xl text-[#2D2B2A]">
                                    <span className={`mr-3 px-2.5 py-1 rounded-full text-xs font-semibold ${badgeFor(r.name)}`}>{r.name}</span>
                                </h2>
                                <p className="text-sm text-[#8A8786]">Y5 = {((r.years[4].units / 10_000_000) * 100).toFixed(2)}% of 10M new diagnoses/yr</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#8A8786] border-b border-[#E5E1D8]/70">
                                            <th className="py-2 pr-4">Year</th>
                                            <th className="py-2 pr-4 text-right">Units (editable)</th>
                                            <th className="py-2 pr-4 text-right">Tier</th>
                                            <th className="py-2 pr-4 text-right">Net /unit</th>
                                            <th className="py-2 pr-4 text-right">Revenue (USD)</th>
                                            <th className="py-2 pr-4 text-right">Net (USD)</th>
                                            <th className="py-2 pr-4 text-right">Net (AUD)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {r.years.map((y, i) => (
                                            <tr key={i} className="border-b border-[#E5E1D8]/40">
                                                <td className="py-2 pr-4 text-[#2D2B2A]">Y{y.year}</td>
                                                <td className="py-2 pr-4 text-right">
                                                    <input type="number" value={y.units} onChange={(e) => setYearUnits(r.name, i, e.target.value)} className="w-28 text-right px-3 py-1.5 rounded-full bg-[#F4F6FB] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none text-[#2D2B2A]" data-testid={`${r.name}-Y${y.year}`} />
                                                </td>
                                                <td className="py-2 pr-4 text-right">
                                                    {y.tierLabel ? (
                                                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#F4F6FB] text-[#6B7DB8] text-[10px] font-semibold uppercase tracking-wider" data-testid={`${r.name}-Y${y.year}-tier`}>{y.tierLabel}</span>
                                                    ) : (
                                                        <span className="text-[10px] text-[#8A8786]">flat</span>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-4 text-right text-[#5C5A59]" data-testid={`${r.name}-Y${y.year}-netper`}>${Math.round(y.netPerUnit)}</td>
                                                <td className="py-2 pr-4 text-right text-[#5C5A59]">{money(y.revenueUsd)}</td>
                                                <td className="py-2 pr-4 text-right text-[#2D2B2A]">{money(y.netUsd)}</td>
                                                <td className="py-2 pr-4 text-right text-[#4A5A95] font-medium">{moneyAud(y.netUsd * fx)}</td>
                                            </tr>
                                        ))}
                                        <tr className="bg-[#F4F6FB] font-medium">
                                            <td className="py-2.5 pr-4 text-[#4A5A95]">5yr total</td>
                                            <td className="py-2.5 pr-4 text-right text-[#2D2B2A]">{r.totalUnits.toLocaleString()}</td>
                                            <td className="py-2.5 pr-4"></td>
                                            <td className="py-2.5 pr-4 text-right text-[#5C5A59]">${Math.round(r.totalNet / Math.max(1, r.totalUnits))}</td>
                                            <td className="py-2.5 pr-4 text-right text-[#2D2B2A]">{money(r.totalRev)}</td>
                                            <td className="py-2.5 pr-4 text-right text-[#2D2B2A]">{money(r.totalNet)}</td>
                                            <td className="py-2.5 pr-4 text-right text-[#4A5A95]">{moneyAud(r.totalNet * fx)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ))}

                    <p className="mt-10 text-sm text-[#8A8786] max-w-3xl">
                        {applyVolumeCogs
                            ? "Net per unit recalculated per year by volume tier. As bundle volume rises, factory MOQ tiers drop tablet+headphones COGS from $135 (pilot) to $52 (1M+) — net per bundle improves from ~$2 to ~$85. Charity 5% is taken from net (after COGS)."
                            : "Net per unit held flat across all years and scenarios. Toggle 'Volume-based COGS scaling' above to model the factory MOQ tiers."}
                        {" "}Even STRETCH stays well under 2% of total global dementia pool.
                    </p>
                </div>
            </main>
        </div>
    );
}

function Slider({ label, value, min, max, step, prefix = "", suffix = "", float = false, onChange, testid, disabled = false }) {
    return (
        <div data-testid={testid} className={disabled ? "opacity-60" : ""}>
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.18em] text-[#6B7DB8] font-semibold">{label}</span>
                <span className="text-sm text-[#2D2B2A] font-medium">{prefix}{float ? Number(value).toFixed(2) : value}{suffix}</span>
            </div>
            <input
                type="range" min={min} max={max} step={step} value={value}
                onChange={(e) => onChange(float ? parseFloat(e.target.value) : parseInt(e.target.value))}
                className="w-full accent-[#6B7DB8] disabled:cursor-not-allowed"
                disabled={disabled}
            />
        </div>
    );
}

function money(usd) {
    if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(2)}B`;
    if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(2)}M`;
    if (usd >= 1_000) return `$${(usd / 1_000).toFixed(0)}K`;
    return `$${Math.round(usd).toLocaleString()}`;
}
function moneyAud(aud) {
    return money(aud).replace("$", "A$");
}
function badgeFor(name) {
    return name === "LOW" ? "bg-[#F4F6FB] text-[#6B7DB8]"
        : name === "MEDIUM" ? "bg-[#8C9ECB] text-white"
        : name === "HIGH" ? "bg-[#4A5A95] text-white"
        : "bg-gradient-to-r from-[#F4D068] to-[#E6B844] text-[#2D2B2A]";
}
